import {
  Button,
  Input,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Textarea,
} from "@roberttamas/ui";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Command } from "cmdk";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { Check, ChevronsUpDown, Loader2 } from "@/components/icons";
import { generateFontScheme, listGoogleFonts } from "@/lib/api";
import { toAppError } from "@/lib/errors";
import { buildGoogleFontsUrl, genericFallback } from "@/lib/fonts";
import type {
  FontCatalogEntry,
  FontScheme,
  GenerateFontSchemeResp,
} from "@/lib/types";

/** Vibe words used by the "I'm feeling lucky" button. */
const VIBE_WORDS = [
  "romantic and elegant",
  "modern minimalist",
  "vintage and classic",
  "rustic garden wedding",
  "bold editorial",
  "soft and timeless",
  "art deco glamour",
  "playful and handwritten",
];

/** Resolves a stored selection to a catalog entry (with variants) or a stub. */
function toEntry(
  family: string,
  category: FontCatalogEntry["category"],
  byName: Map<string, FontCatalogEntry>,
): FontCatalogEntry {
  return (
    byName.get(family.toLowerCase()) ?? {
      family,
      category,
      variants: ["regular", "700"],
    }
  );
}

/** Searchable Google Fonts picker backed by cmdk + a popover. */
function FontCombobox({
  label,
  fonts,
  value,
  onPick,
  disabled,
}: {
  label: string;
  fonts: FontCatalogEntry[];
  value: FontCatalogEntry | null;
  onPick: (entry: FontCatalogEntry) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const base = needle
      ? fonts.filter((f) => f.family.toLowerCase().includes(needle))
      : fonts;
    return base.slice(0, 80);
  }, [query, fonts]);

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            className="w-full justify-between font-normal"
          >
            <span className="truncate">{value?.family ?? t("fontEditor.selectFont")}</span>
            <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[var(--radix-popover-trigger-width)] p-0"
          align="start"
        >
          <Command shouldFilter={false} className="overflow-hidden">
            <div className="border-b px-2">
              <Command.Input
                value={query}
                onValueChange={setQuery}
                placeholder={t("fontEditor.searchFonts")}
                className="h-10 w-full bg-transparent text-base outline-none placeholder:text-muted-foreground"
              />
            </div>
            <Command.List className="ui-scrollbar max-h-64 overflow-y-auto p-1">
              <Command.Empty className="px-3 py-6 text-center text-sm text-muted-foreground">
                {t("fontEditor.noFonts")}
              </Command.Empty>
              {filtered.map((font) => (
                <Command.Item
                  key={font.family}
                  value={font.family}
                  onSelect={() => {
                    onPick(font);
                    setOpen(false);
                  }}
                  className="flex cursor-pointer items-center justify-between gap-2 rounded-sm px-2 py-1.5 text-sm data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground"
                >
                  <span className="truncate">{font.family}</span>
                  <span className="flex items-center gap-2">
                    <span className="text-[11px] text-muted-foreground">{font.category}</span>
                    {value?.family === font.family && <Check className="h-4 w-4" />}
                  </span>
                </Command.Item>
              ))}
            </Command.List>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}

/** Live sample rendered with the currently-selected pairing. */
export function FontPreview({
  heading,
  body,
  cssUrl,
  loading,
}: {
  heading: FontCatalogEntry | null;
  body: FontCatalogEntry | null;
  cssUrl: string | null;
  loading?: boolean;
}) {
  // Inject (and keep in sync) a single preview stylesheet for the dialog.
  useEffect(() => {
    if (!cssUrl) return;
    const id = "font-scheme-preview";
    let link = document.getElementById(id) as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement("link");
      link.id = id;
      link.rel = "stylesheet";
      document.head.appendChild(link);
    }
    if (link.href !== cssUrl) link.href = cssUrl;
    return () => {
      document.getElementById(id)?.remove();
    };
  }, [cssUrl]);

  const headingStack = heading
    ? `"${heading.family}", ${genericFallback(heading.category)}`
    : undefined;
  const bodyStack = body
    ? `"${body.family}", ${genericFallback(body.category)}`
    : undefined;

  return (
    <div className="relative space-y-3 rounded-lg border bg-muted/30 p-5 text-center">
      {loading && (
        <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-background/60">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      )}
      <p
        className="text-3xl"
        style={{ fontFamily: headingStack, fontWeight: 400 }}
      >
        Daniela &amp; Kamil
      </p>
      <p
        className="text-sm text-muted-foreground"
        style={{ fontFamily: bodyStack, fontWeight: 300 }}
      >
        Share your favourite moments from our special day. Upload your photos
        and videos here.
      </p>
    </div>
  );
}

interface FontSchemeEditorProps {
  value: FontScheme;
  onChange: (next: FontScheme) => void;
}

/**
 * Controlled font-pairing editor: describe a vibe for an AI suggestion or pick
 * a heading + body from the full Google Fonts catalog. Emits a complete
 * `FontScheme` (with a prebuilt `cssUrl`) on every change.
 */
export function FontSchemeEditor({ value, onChange }: FontSchemeEditorProps) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<"ai" | "manual">("ai");
  const [heading, setHeading] = useState<FontCatalogEntry | null>(null);
  const [body, setBody] = useState<FontCatalogEntry | null>(null);
  const [aiMessage, setAiMessage] = useState("");
  const [aiSeed, setAiSeed] = useState("");

  const fontsQuery = useQuery({
    queryKey: ["googleFonts"],
    queryFn: listGoogleFonts,
    staleTime: 60 * 60_000,
  });
  const fonts = useMemo(() => fontsQuery.data?.fonts ?? [], [fontsQuery.data]);
  const byName = useMemo(() => {
    const map = new Map<string, FontCatalogEntry>();
    for (const f of fonts) map.set(f.family.toLowerCase(), f);
    return map;
  }, [fonts]);

  // Once the catalog loads, hydrate the editable selections from the incoming
  // value so manual editing has real variant metadata.
  useEffect(() => {
    if (fonts.length === 0) return;
    setHeading((prev) =>
      prev ?? toEntry(value.heading.family, value.heading.category, byName),
    );
    setBody((prev) =>
      prev ?? toEntry(value.body.family, value.body.category, byName),
    );
  }, [fonts, byName, value]);

  const previewUrl = useMemo(
    () => (heading && body ? buildGoogleFontsUrl(heading, body) : value.cssUrl),
    [heading, body, value.cssUrl],
  );

  const emit = (h: FontCatalogEntry | null, b: FontCatalogEntry | null) => {
    if (!h || !b) return;
    onChange({
      heading: { family: h.family, category: h.category },
      body: { family: b.family, category: b.category },
      cssUrl: buildGoogleFontsUrl(h, b),
    });
  };

  const pickHeading = (entry: FontCatalogEntry) => {
    setHeading(entry);
    emit(entry, body);
  };

  const pickBody = (entry: FontCatalogEntry) => {
    setBody(entry);
    emit(heading, entry);
  };

  const aiMutation = useMutation<
    GenerateFontSchemeResp,
    unknown,
    { message?: string; seed?: string }
  >({
    mutationFn: (input) => generateFontScheme(input),
    onSuccess: (resp) => {
      const h = toEntry(
        resp.fontScheme.heading.family,
        resp.fontScheme.heading.category,
        byName,
      );
      const b = toEntry(
        resp.fontScheme.body.family,
        resp.fontScheme.body.category,
        byName,
      );
      setHeading(h);
      setBody(b);
      emit(h, b);
      toast.success(t("fontEditor.aiPairing"));
    },
    onError: (err) => toast.error(toAppError(err).message),
  });

  const generateWithAi = () => {
    const message = aiMessage.trim();
    const seed = aiSeed.trim();
    if (!message && !seed) {
      toast.error(t("fontEditor.aiNeedInput"));
      return;
    }
    aiMutation.mutate({ message: message || undefined, seed: seed || undefined });
  };

  const feelingLucky = () => {
    const word =
      VIBE_WORDS[Math.floor(Math.random() * VIBE_WORDS.length)] ??
      "romantic and elegant";
    setAiSeed("");
    setAiMessage(word);
    aiMutation.mutate({ message: word });
  };

  const catalogLoading = fontsQuery.isLoading;

  return (
    <div className="space-y-4">
      <div className="inline-flex rounded-md border p-0.5">
        <Button
          type="button"
          size="sm"
          variant={mode === "ai" ? "default" : "ghost"}
          onClick={() => setMode("ai")}
        >
          AI
        </Button>
        <Button
          type="button"
          size="sm"
          variant={mode === "manual" ? "default" : "ghost"}
          onClick={() => setMode("manual")}
        >
          {t("fontEditor.modeManual")}
        </Button>
      </div>

      {mode === "ai" && (
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="font-ai-message">{t("aiTools.describeVibe")}</Label>
            <Textarea
              id="font-ai-message"
              rows={3}
              placeholder={t("fontEditor.aiPlaceholder")}
              value={aiMessage}
              onChange={(e) => setAiMessage(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              {t("fontEditor.aiHint")}
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="font-ai-seed">{t("fontEditor.favouriteFont")}</Label>
            <Input
              id="font-ai-seed"
              placeholder={t("fontEditor.favouritePlaceholder")}
              value={aiSeed}
              onChange={(e) => setAiSeed(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-2 sm:flex-row">
            <Button
              type="button"
              variant="outline"
              className="sm:flex-1"
              onClick={feelingLucky}
              disabled={aiMutation.isPending}
            >
              {t("aiTools.feelingLucky")}
            </Button>
            <Button
              type="button"
              className="sm:flex-1"
              onClick={generateWithAi}
              disabled={aiMutation.isPending || (!aiMessage.trim() && !aiSeed.trim())}
            >
              {aiMutation.isPending ? t("aiTools.generating") : t("aiTools.generateWithAi")}
            </Button>
          </div>
          <FontPreview
            heading={heading}
            body={body}
            cssUrl={previewUrl}
            loading={aiMutation.isPending}
          />
        </div>
      )}

      {mode === "manual" && (
        <div className="space-y-4">
          {catalogLoading ? (
            <div className="flex items-center justify-center gap-2 rounded-md border py-8 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> {t("fontEditor.loadingFonts")}
            </div>
          ) : (
            <>
              <FontCombobox
                label={t("fontEditor.headingFont")}
                fonts={fonts}
                value={heading}
                onPick={pickHeading}
              />
              <FontCombobox
                label={t("fontEditor.bodyFont")}
                fonts={fonts}
                value={body}
                onPick={pickBody}
              />
            </>
          )}
          <FontPreview heading={heading} body={body} cssUrl={previewUrl} />
        </div>
      )}
    </div>
  );
}
